import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useClientType } from '../../hooks/use-client-type';
import { useTenantCountryCode } from '../../hooks/use-tenant-country-code';
import {
  LanguageCode,
  TranslateParams,
  I18nContextValue,
} from '../../types/i18n/i18n.type';
import {
  determineInitialLanguage,
  interpolate,
  resolveTranslationValue,
  LANGUAGE_STORAGE_KEY,
} from '../../utils/i18n.util';
import { I18nContext } from './i18n-context-instance';

/**
 * Provider component for internationalization (i18n) context.
 *
 * Manages the current language, provides translation utilities, and handles
 * language persistence to localStorage. Must wrap components that use the
 * useI18n hook.
 *
 * @param props - Component props
 * @param props.children - Child components to wrap with i18n context
 *
 * @example
 * ```tsx
 * <I18nProvider>
 *   <App />
 * </I18nProvider>
 * ```
 */
export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<LanguageCode>(
    determineInitialLanguage
  );
  const clientType = useClientType();
  const tenantCountryCode = useTenantCountryCode();

  const changeLanguage = useCallback((next: LanguageCode) => {
    setLanguage(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    }
  }, []);

  const translate = useCallback(
    (key: string, params?: TranslateParams) => {
      const text = resolveTranslationValue(language, key, {
        clientType,
        tenantCountryCode,
        allowClientOverrides: true,
      });
      return interpolate(text, params);
    },
    [language, clientType, tenantCountryCode]
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage: changeLanguage,
      clientType,
      t: translate,
    }),
    [language, changeLanguage, clientType, translate]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
